import { env } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { withStableCacheBust } from "./cacheBuster";
import { ensureAbsoluteImageUrl, toDirectPublicStorageUrl } from "./imageOptimization";

const OBJECT_PUBLIC_MARKER = "/storage/v1/object/public/";
const RENDER_PUBLIC_MARKER = "/storage/v1/render/image/public/";

/** Activar en `.env`: `VITE_SUPABASE_IMAGE_TRANSFORMS=true` (requiere Image Transformation en Supabase). */
export function areSupabaseImageTransformsEnabled(): boolean {
  return import.meta.env.VITE_SUPABASE_IMAGE_TRANSFORMS === "true";
}

/**
 * Presets de tamaño para `/storage/v1/render/image/public/...`
 * (anchos en px lógicos × ~2 para retina en thumbs/listas).
 */
export const IMAGE_PRESETS = {
  listThumb: { width: 168, height: 210, quality: 80, format: "webp" as const, resize: "cover" as const },
  card: { width: 640, height: 640, quality: 82, format: "webp" as const, resize: "cover" as const },
  cardHero: { width: 640, height: 640, quality: 85, format: "webp" as const, resize: "cover" as const },
  detail: { width: 800, height: 800, quality: 85, format: "webp" as const, resize: "cover" as const },
  avatar: { width: 160, height: 160, quality: 80, format: "webp" as const, resize: "cover" as const },
  profileCover: { width: 800, height: 400, quality: 82, format: "webp" as const, resize: "cover" as const },
  flyerContain: { width: 640, height: 640, quality: 82, format: "webp" as const, resize: "contain" as const },
} as const;

export type ImagePreset = keyof typeof IMAGE_PRESETS;

/**
 * `bucket/path/to/file` desde una URL absoluta de Storage (object o render).
 */
export function extractStoragePublicPath(url: string): string | null {
  const trimmed = url.trim().split("?")[0];
  let idx = trimmed.indexOf(OBJECT_PUBLIC_MARKER);
  if (idx >= 0) {
    return trimmed.slice(idx + OBJECT_PUBLIC_MARKER.length).replace(/^\/+/, "").replace(/\/+$/, "") || null;
  }
  idx = trimmed.indexOf(RENDER_PUBLIC_MARKER);
  if (idx >= 0) {
    return trimmed.slice(idx + RENDER_PUBLIC_MARKER.length).replace(/^\/+/, "").replace(/\/+$/, "") || null;
  }
  return null;
}

/**
 * URL con Image Transformation de Supabase.
 * Si las transformaciones están deshabilitadas o la URL no es Storage público, devuelve la URL directa con cache-bust.
 */
export function buildOptimizedImageUrl(
  rawUrl: string | null | undefined,
  preset: ImagePreset,
  version?: string | number | null,
): string | null {
  if (rawUrl == null || String(rawUrl).trim() === "") return null;

  const trimmed = String(rawUrl).trim();
  const absolute =
    toDirectPublicStorageUrl(ensureAbsoluteImageUrl(trimmed) ?? trimmed) ?? ensureAbsoluteImageUrl(trimmed);
  if (!absolute) return null;

  const clean = absolute.split("?")[0];
  const bustedFallback = withStableCacheBust(clean, version ?? null) ?? clean;

  if (!areSupabaseImageTransformsEnabled()) {
    return bustedFallback;
  }

  let origin = (env.supabase.url || "").replace(/\/+$/, "");
  try {
    origin = new URL(clean).origin;
  } catch {
    if (!origin) return bustedFallback;
  }

  const publicPath = extractStoragePublicPath(clean);
  if (!publicPath || !origin) {
    return bustedFallback;
  }

  const cfg = IMAGE_PRESETS[preset];
  const params = new URLSearchParams({
    width: String(cfg.width),
    height: String(cfg.height),
    quality: String(cfg.quality),
    resize: cfg.resize,
    format: cfg.format,
  });

  const renderUrl = `${origin}${RENDER_PUBLIC_MARKER}${publicPath}?${params.toString()}`;
  return withStableCacheBust(renderUrl, version ?? null) ?? renderUrl;
}

export function buildResizedImageUrl(
  rawUrl: string | null | undefined,
  options: {
    width: number;
    height?: number;
    quality?: number;
    format?: "webp" | "origin";
    resize?: "cover" | "contain" | "fill";
    version?: string | number | null;
  },
): string | null {
  if (rawUrl == null || String(rawUrl).trim() === "") return null;

  const trimmed = String(rawUrl).trim();
  const absolute =
    toDirectPublicStorageUrl(ensureAbsoluteImageUrl(trimmed) ?? trimmed) ?? ensureAbsoluteImageUrl(trimmed);
  if (!absolute) return null;

  const clean = absolute.split("?")[0];
  const bustedFallback = withStableCacheBust(clean, options.version ?? null) ?? clean;

  if (!areSupabaseImageTransformsEnabled()) {
    return bustedFallback;
  }

  let origin = (env.supabase.url || "").replace(/\/+$/, "");
  try {
    origin = new URL(clean).origin;
  } catch {
    if (!origin) return bustedFallback;
  }

  const publicPath = extractStoragePublicPath(clean);
  if (!publicPath || !origin) return bustedFallback;

  const quality = options.quality ?? 80;
  const format = options.format ?? "webp";
  const resize = options.resize ?? "cover";

  const params = new URLSearchParams({
    width: String(options.width),
    quality: String(quality),
    resize,
    format,
  });
  if (options.height != null) params.set("height", String(options.height));

  const renderUrl = `${origin}${RENDER_PUBLIC_MARKER}${publicPath}?${params.toString()}`;
  return withStableCacheBust(renderUrl, options.version ?? null) ?? renderUrl;
}

type BuildStoragePublicUrlOptions = {
  bucket?: string;
  version?: string | number | null;
};

export function buildSupabaseStoragePublicUrl(
  path: string,
  options?: BuildStoragePublicUrlOptions
): string {
  const bucket = options?.bucket || "media";
  const baseUrl = supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  return withStableCacheBust(baseUrl, options?.version ?? null) ?? baseUrl;
}

/**
 * Resolves a Supabase Storage reference to a public URL.
 *
 * Supports:
 * - Full URLs (http/https), data: URIs, and absolute paths ("/...") → returned as-is
 * - "bucket/path" → uses that bucket
 * - "avatars/..." (or any "folder/...") without bucket → assumes default bucket ("media")
 * - "filename.ext" without slashes → assumes default bucket ("media")
 *
 * This avoids bugs where "avatars/..." was misread as bucket="avatars" and produced truncated/broken URLs.
 */
export function resolveSupabaseStoragePublicUrl(
  maybePath?: string | null,
  options?: { defaultBucket?: string }
): string | undefined {
  if (!maybePath) return undefined;
  const v = String(maybePath).trim();
  if (!v) return undefined;

  // Already a URL / absolute path → normalizar bucket erróneo AVATARS (debe ser media/avatars)
  if (/^https?:\/\//i.test(v) || v.startsWith("data:") || v.startsWith("/")) {
    if (v.includes("/storage/v1/object/public/AVATARS/")) {
      return v.replace("/storage/v1/object/public/AVATARS/", "/storage/v1/object/public/media/avatars/");
    }
    return v;
  }

  const defaultBucket = options?.defaultBucket || "media";
  const knownBuckets = new Set([defaultBucket, "media"]);

  let bucket = defaultBucket;
  let path = v;

  const slash = v.indexOf("/");
  if (slash > 0) {
    const first = v.slice(0, slash);
    const rest = v.slice(slash + 1);
    if (knownBuckets.has(first)) {
      bucket = first;
      path = rest;
    } else {
      // Treat entire string as a path inside default bucket (e.g. "avatars/...")
      bucket = defaultBucket;
      // Normalizar bucket erróneo en path: AVATARS/xxx → avatars/xxx
      path = first.toLowerCase() === "avatars" ? `avatars/${rest}` : v;
    }
  }

  try {
    return buildSupabaseStoragePublicUrl(path, { bucket });
  } catch {
    return v;
  }
}

export function resolveVersionedSupabaseStoragePublicUrl(
  maybePath?: string | null,
  version?: string | number | null,
  options?: { defaultBucket?: string }
): string | undefined {
  const resolved = resolveSupabaseStoragePublicUrl(maybePath, options);
  return withStableCacheBust(resolved, version ?? null) ?? resolved;
}

export function resolveVersionedSupabaseStorageDirectUrl(
  maybePath?: string | null,
  version?: string | number | null,
  options?: { defaultBucket?: string }
): string | undefined {
  const resolved = resolveVersionedSupabaseStoragePublicUrl(maybePath, version, options);
  return toDirectPublicStorageUrl(resolved) ?? resolved;
}

